/* STAS Admin — Portal Campaigns Management.
 * V1 scope: landing page campaigns (banner / card / video_card / popup / carousel_item).
 *
 * Layout: master-detail split. List on the right (RTL), edit form + live preview on the left.
 *
 * Permissions: STAS-only — gated by backend (USERS_CREATE permission). Other users land here
 * but every API call returns 403.
 */
import { useEffect, useRef, useState } from "react";
import {
  Plus, Edit3, Trash2, Save, X, Image as ImageIcon, Video, Upload, Eye, ToggleLeft, ToggleRight, Calendar,
} from "lucide-react";

import { api } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { useBranding } from "@/contexts/BrandingContext";
import { PORTAL_T } from "@/lib/portalSubmitI18n";
import { PortalMockup, TEMPLATES, PLACEMENTS } from "@/components/portal/CampaignTemplates";
import PortalMusicPanel from "@/components/portal/PortalMusicPanel";

const EMPTY = {
  title_ar: "", title_en: "",
  body_ar: "",  body_en: "",
  template: "banner", placement: "landing_top",
  image_path: null, image_url: null,
  images: [], image_urls: [],
  slide_interval_seconds: 5,
  slide_direction: "rtl",
  video_path: null, video_url: null,
  video_external_url: "",
  image_fit: "contain",
  cta_label_ar: "", cta_label_en: "", cta_url: "",
  start_at: "", end_at: "",
  is_active: true, weight: 100,
};

export default function PortalCampaignsAdminPage() {
  const { theme, dir, lang } = useI18n();
  const { branding } = useBranding();
  const css = makeStyles(theme, dir);

  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null = no editor open; {} = new; {...row} = edit
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/portal-campaigns");
      setItems(data.items || []);
    } catch (e) { setErr(e?.response?.data?.detail || "تعذّر تحميل القائمة"); }
  };
  useEffect(() => { reload(); }, []);

  const startCreate = () => { setErr(""); setEditing({ ...EMPTY }); };
  const startEdit = (row) => { setErr(""); setEditing({ ...EMPTY, ...row }); };

  const removeRow = async (row) => {
    if (!window.confirm(`حذف "${row.title_ar}"؟ لا يمكن التراجع.`)) return;
    try {
      await api.delete(`/admin/portal-campaigns/${row.id}`);
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || "تعذّر الحذف"); }
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {
        title_ar: editing.title_ar?.trim() || null,
        title_en: editing.title_en?.trim() || null,
        body_ar: editing.body_ar?.trim() || null,
        body_en: editing.body_en?.trim() || null,
        template: editing.template,
        placement: editing.placement,
        image_path: editing.image_path || null,
        images: editing.images || [],
        slide_interval_seconds: Number(editing.slide_interval_seconds) || 5,
        slide_direction: editing.slide_direction || "rtl",
        video_path: editing.video_path || null,
        video_external_url: editing.video_external_url?.trim() || null,
        image_fit: editing.image_fit || "contain",
        cta_label_ar: editing.cta_label_ar?.trim() || null,
        cta_label_en: editing.cta_label_en?.trim() || null,
        cta_url: editing.cta_url?.trim() || null,
        start_at: editing.start_at || null,
        end_at: editing.end_at || null,
        is_active: !!editing.is_active,
        weight: Number(editing.weight) || 100,
      };
      // At least ONE of image / slideshow / video / title is required.
      const hasContent = !!(payload.image_path || (payload.images && payload.images.length)
        || payload.video_path || payload.video_external_url || payload.title_ar || payload.title_en);
      if (!hasContent) {
        setErr("أضف على الأقل صورة أو فيديو أو عنوان");
        setBusy(false); return;
      }
      if (editing.id) await api.patch(`/admin/portal-campaigns/${editing.id}`, payload);
      else await api.post("/admin/portal-campaigns", payload);
      setEditing(null);
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };

  return (
    <div dir={dir} style={css.page} data-testid="campaigns-admin-page">
        <div style={css.header}>
          <div>
            <h1 style={css.h1}>إدارة إعلانات البوابة</h1>
            <p style={css.sub}>أنشئ إعلانات، عروض، تهاني وفيديوهات تظهر على البوابة بدون أي تعديل برمجي.</p>
          </div>
          <button onClick={startCreate} style={css.btnGold} data-testid="campaigns-new-btn">
            <Plus size={16} strokeWidth={2.6} /><span>إعلان جديد</span>
          </button>
        </div>

        {err && <div style={css.err} data-testid="campaigns-error">{err}</div>}

        <PortalMusicPanel theme={theme} dir={dir} css={css} />

        <div style={css.split}>
          {/* ───── List ───── */}
          <div style={css.list} data-testid="campaigns-list">
            {items.length === 0 && (
              <div style={css.empty}>لا توجد إعلانات بعد — اضغط "إعلان جديد".</div>
            )}
            {items.map((row) => (
              <CampaignRow
                key={row.id} row={row} theme={theme} css={css}
                onEdit={() => startEdit(row)} onDelete={() => removeRow(row)}
              />
            ))}
          </div>

          {/* ───── Editor + Preview ───── */}
          {editing && (
            <CampaignEditor
              editing={editing} setEditing={setEditing}
              theme={theme} css={css} lang={lang} dir={dir}
              branding={branding}
              busy={busy} onCancel={() => setEditing(null)} onSave={save}
              setErr={setErr}
            />
          )}
        </div>
      </div>
  );
}

/* ───── List row ───── */
function CampaignRow({ row, theme, css, onEdit, onDelete }) {
  const tmpl = TEMPLATES.find((t) => t.code === row.template) || TEMPLATES[0];
  const place = PLACEMENTS.find((p) => p.code === row.placement) || PLACEMENTS[0];
  return (
    <div style={css.row} data-testid={`campaign-row-${row.id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
        {row.image_url ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${row.image_url}`} alt="" style={css.thumb} />
        ) : (
          <div style={{ ...css.thumb, display: "inline-flex", alignItems: "center", justifyContent: "center", background: theme.surfaceAlt }}>
            <ImageIcon size={20} color={theme.textMuted} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 900, fontSize: 14, color: theme.text }}>{row.title_ar}</div>
          <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
            {tmpl.label_ar} · {place.label_ar}
          </div>
        </div>
      </div>
      <span style={{
        padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 900,
        background: row.is_active ? "rgba(22,163,74,0.14)" : theme.surfaceAlt,
        color: row.is_active ? "#16a34a" : theme.textMuted,
      }}>{row.is_active ? "نشط" : "متوقف"}</span>
      <button onClick={onEdit} style={css.iconBtn} data-testid={`campaign-edit-${row.id}`}>
        <Edit3 size={14} strokeWidth={2.4} />
      </button>
      <button onClick={onDelete} style={css.iconBtnDanger} data-testid={`campaign-delete-${row.id}`}>
        <Trash2 size={14} strokeWidth={2.4} />
      </button>
    </div>
  );
}

/* ───── Editor + Live Preview ───── */
function CampaignEditor({ editing, setEditing, theme, css, lang, dir, branding, busy, onCancel, onSave, setErr }) {
  const u = (k, v) => setEditing((s) => ({ ...s, [k]: v }));
  const [previewLang, setPreviewLang] = useState("ar");
  const T = PORTAL_T[previewLang];

  const fileImg = useRef(null);
  const fileSlides = useRef(null);
  const fileVid = useRef(null);
  const [uploadBusy, setUploadBusy] = useState(false);

  const upload = async (kind, fileInput) => {
    const f = fileInput.current?.files?.[0];
    if (!f) return;
    setUploadBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", f);
      const { data } = await api.post("/admin/portal-campaigns/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (kind === "image") {
        u("image_path", data.storage_path);
        u("image_url", URL.createObjectURL(f));
      } else {
        u("video_path", data.storage_path);
        u("video_url", URL.createObjectURL(f));
      }
      fileInput.current.value = "";
    } catch (e) { setErr(e?.response?.data?.detail || "فشل الرفع"); }
    finally { setUploadBusy(false); }
  };

  // Multi-image slideshow uploader — adds each picked file to the `images` array.
  const uploadSlides = async () => {
    const files = Array.from(fileSlides.current?.files || []);
    if (!files.length) return;
    const remaining = 10 - ((editing.images || []).length);
    if (remaining <= 0) {
      setErr("الحد الأقصى 10 صور لكل إعلان");
      fileSlides.current.value = "";
      return;
    }
    setUploadBusy(true); setErr("");
    const toUpload = files.slice(0, remaining);
    try {
      const newPaths = [...(editing.images || [])];
      const newPreviews = [...(editing.image_urls || [])];
      for (const f of toUpload) {
        const fd = new FormData();
        fd.append("kind", "image");
        fd.append("file", f);
        const { data } = await api.post("/admin/portal-campaigns/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newPaths.push(data.storage_path);
        newPreviews.push(URL.createObjectURL(f));
      }
      u("images", newPaths);
      u("image_urls", newPreviews);
      fileSlides.current.value = "";
    } catch (e) { setErr(e?.response?.data?.detail || "فشل رفع الصور"); }
    finally { setUploadBusy(false); }
  };

  const removeSlide = (idx) => {
    const imgs = (editing.images || []).filter((_, i) => i !== idx);
    const urls = (editing.image_urls || []).filter((_, i) => i !== idx);
    u("images", imgs);
    u("image_urls", urls);
  };

  const moveSlide = (idx, dir) => {
    const swap = idx + dir;
    if (swap < 0 || swap >= (editing.images || []).length) return;
    const imgs = [...(editing.images || [])];
    const urls = [...(editing.image_urls || [])];
    [imgs[idx], imgs[swap]] = [imgs[swap], imgs[idx]];
    [urls[idx], urls[swap]] = [urls[swap], urls[idx]];
    u("images", imgs);
    u("image_urls", urls);
  };

  // The shape sent to the preview component (uses *_url for src — same as runtime).
  const previewCampaign = {
    ...editing,
    id: editing.id || "preview",
    image_url: editing.image_url || null,
    video_url: editing.video_url || null,
  };

  return (
    <div style={css.editor} data-testid="campaign-editor">
      <div style={css.editorHeader}>
        <h2 style={{ ...css.h2, margin: 0 }}>{editing.id ? "تعديل إعلان" : "إعلان جديد"}</h2>
        <button onClick={onCancel} style={css.iconBtn}><X size={16} /></button>
      </div>

      <div style={css.form}>
        <Field label="القالب" theme={theme}>
          <select value={editing.template} onChange={(e) => u("template", e.target.value)}
            style={css.input} data-testid="campaign-template-select">
            {TEMPLATES.map((t) => <option key={t.code} value={t.code}>{t.label_ar}</option>)}
          </select>
        </Field>
        <Field label="مكان الظهور" theme={theme}>
          <select value={editing.placement} onChange={(e) => u("placement", e.target.value)}
            style={css.input} data-testid="campaign-placement-select">
            {PLACEMENTS.map((p) => <option key={p.code} value={p.code}>{p.label_ar}</option>)}
          </select>
        </Field>

        <Field label="العنوان (عربي) — اختياري" theme={theme}>
          <input value={editing.title_ar} onChange={(e) => u("title_ar", e.target.value)}
            style={css.input} placeholder="مثال: عرض الصيف (اتركه فارغاً لو الصورة كافية)" data-testid="campaign-title-ar" />
        </Field>
        <Field label="العنوان (English)" theme={theme}>
          <input value={editing.title_en} onChange={(e) => u("title_en", e.target.value)}
            style={{ ...css.input, direction: "ltr" }} placeholder="Summer Offer" data-testid="campaign-title-en" />
        </Field>

        <Field label="الوصف (عربي)" theme={theme}>
          <textarea value={editing.body_ar} onChange={(e) => u("body_ar", e.target.value)} rows={2}
            style={{ ...css.input, resize: "vertical" }} placeholder="خصم 50% على رسوم المعقّب" data-testid="campaign-body-ar" />
        </Field>
        <Field label="الوصف (English)" theme={theme}>
          <textarea value={editing.body_en} onChange={(e) => u("body_en", e.target.value)} rows={2}
            style={{ ...css.input, direction: "ltr", resize: "vertical" }} placeholder="50% off muaqqib fees" data-testid="campaign-body-en" />
        </Field>

        {/* Upload single image (legacy / quick mode) */}
        <Field label="صورة واحدة (PNG/JPG/WebP — 1200×675 px مفضّل — ≤8 ميغا)" theme={theme}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" ref={fileImg}
              onChange={() => upload("image", fileImg)} style={{ display: "none" }}
              data-testid="campaign-image-file" />
            <button onClick={() => fileImg.current?.click()} style={css.btnSecondary} disabled={uploadBusy}
              data-testid="campaign-image-upload">
              <Upload size={14} strokeWidth={2.4} /> {uploadBusy ? "جارٍ الرفع…" : "رفع صورة واحدة"}
            </button>
            {editing.image_path && (
              <button onClick={() => { u("image_path", null); u("image_url", null); }} style={css.btnGhostSmall}>
                إزالة
              </button>
            )}
            {editing.image_url && (
              <img src={editing.image_url.startsWith("blob:") ? editing.image_url : `${process.env.REACT_APP_BACKEND_URL}${editing.image_url}`}
                alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover" }} />
            )}
          </div>
        </Field>

        {/* Multi-image slideshow */}
        <Field label={`سلايدر متعدد الصور (حتى 10) — تعمل تلقائياً وتتقدّم بين الصور`} theme={theme}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
            <input type="file" accept="image/png,image/jpeg,image/webp" ref={fileSlides}
              multiple onChange={uploadSlides} style={{ display: "none" }}
              data-testid="campaign-slides-file" />
            <button onClick={() => fileSlides.current?.click()} style={css.btnSecondary} disabled={uploadBusy}
              data-testid="campaign-slides-upload">
              <Upload size={14} strokeWidth={2.4} />
              {uploadBusy ? "جارٍ الرفع…" : `إضافة صور للسلايدر (${(editing.images || []).length}/10)`}
            </button>
            <span style={{ fontSize: 10, color: theme.textMuted }}>
              💡 لو رفعت أكثر من صورة، تشتغل تلقائياً كسلايدر متحرك
            </span>
          </div>
          {(editing.images || []).length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 8 }}>
              {(editing.image_urls || editing.images || []).map((url, idx) => (
                <div key={idx} style={{
                  position: "relative", aspectRatio: "16/9",
                  borderRadius: 8, overflow: "hidden",
                  background: theme.surfaceAlt, border: `1px solid ${theme.border}`,
                }} data-testid={`campaign-slide-${idx}`}>
                  <img
                    src={typeof url === "string" && url.startsWith("blob:") ? url
                      : typeof url === "string" && url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${url}`
                      : url}
                    alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <div style={{
                    position: "absolute", top: 3, [dir === "rtl" ? "left" : "right"]: 3,
                    display: "flex", gap: 2,
                  }}>
                    <button onClick={() => moveSlide(idx, -1)} disabled={idx === 0} title="إلى الأمام"
                      style={{
                        width: 22, height: 22, borderRadius: 4, border: "none", cursor: idx === 0 ? "default" : "pointer",
                        background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 11, fontWeight: 800,
                        opacity: idx === 0 ? 0.3 : 1,
                      }}>‹</button>
                    <button onClick={() => moveSlide(idx, 1)} disabled={idx === (editing.images || []).length - 1} title="إلى الخلف"
                      style={{
                        width: 22, height: 22, borderRadius: 4, border: "none",
                        cursor: idx === (editing.images || []).length - 1 ? "default" : "pointer",
                        background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 11, fontWeight: 800,
                        opacity: idx === (editing.images || []).length - 1 ? 0.3 : 1,
                      }}>›</button>
                  </div>
                  <button onClick={() => removeSlide(idx)} title="حذف"
                    data-testid={`campaign-slide-remove-${idx}`}
                    style={{
                      position: "absolute", bottom: 3, [dir === "rtl" ? "left" : "right"]: 3,
                      width: 22, height: 22, borderRadius: 4, border: "none", cursor: "pointer",
                      background: "rgba(220,38,38,0.85)", color: "#fff", fontSize: 14,
                    }}>×</button>
                  <span style={{
                    position: "absolute", top: 3, [dir === "rtl" ? "right" : "left"]: 3,
                    background: "rgba(0,0,0,0.7)", color: "#fff", padding: "1px 6px",
                    borderRadius: 999, fontSize: 9, fontWeight: 800,
                  }}>{idx + 1}</span>
                </div>
              ))}
            </div>
          )}
        </Field>

        {/* Slider settings — only shown when there are multiple images */}
        {(editing.images || []).length > 1 && (
          <div style={{
            padding: 12, borderRadius: 10, background: theme.surfaceAlt,
            border: `1px dashed ${theme.borderAccent}`,
          }}>
            <div style={{ fontSize: 11, fontWeight: 900, color: theme.gold, marginBottom: 10 }}>
              ⚙️ إعدادات السلايدر
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="مدة كل صورة (بالثواني)" theme={theme}>
                <input type="number" min={1} max={300}
                  value={editing.slide_interval_seconds}
                  onChange={(e) => u("slide_interval_seconds", e.target.value)}
                  style={css.input} data-testid="campaign-slide-interval" />
              </Field>
              <Field label="اتجاه التحرّك" theme={theme}>
                <div style={{ display: "flex", gap: 6, padding: 3, background: theme.surface, borderRadius: 999, width: "fit-content" }}>
                  {[
                    { code: "rtl", label_ar: "← يدخل من اليمين" },
                    { code: "ltr", label_ar: "يدخل من اليسار →" },
                  ].map((opt) => (
                    <button key={opt.code} type="button" onClick={() => u("slide_direction", opt.code)}
                      data-testid={`campaign-slide-direction-${opt.code}`}
                      style={{
                        padding: "5px 11px", borderRadius: 999, fontSize: 10, fontWeight: 800,
                        border: "none", cursor: "pointer",
                        background: (editing.slide_direction || "rtl") === opt.code ? theme.gold : "transparent",
                        color: (editing.slide_direction || "rtl") === opt.code ? "#0a0a0a" : theme.text,
                      }}>{opt.label_ar}</button>
                  ))}
                </div>
              </Field>
            </div>
          </div>
        )}

        {/* Image fit toggle */}
        <Field label="طريقة عرض الصورة داخل الإطار" theme={theme}>
          <div style={{ display: "flex", gap: 6, padding: 4, background: theme.surfaceAlt, borderRadius: 999, width: "fit-content" }} data-testid="campaign-image-fit">
            {[
              { code: "contain", label_ar: "عرض كامل (بدون قص)" },
              { code: "cover",   label_ar: "ملء الإطار (قد يُقتطع)" },
            ].map((opt) => (
              <button key={opt.code} type="button" onClick={() => u("image_fit", opt.code)}
                data-testid={`campaign-image-fit-${opt.code}`}
                style={{
                  padding: "6px 14px", borderRadius: 999, fontSize: 11, fontWeight: 800,
                  border: "none", cursor: "pointer",
                  background: (editing.image_fit || "contain") === opt.code ? theme.gold : "transparent",
                  color: (editing.image_fit || "contain") === opt.code ? "#0a0a0a" : theme.text,
                }}>{opt.label_ar}</button>
            ))}
          </div>
          <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 6 }}>
            💡 الأبعاد المثالية للمصمم: <strong dir="ltr" style={{ color: theme.gold }}>1200 × 675 px</strong> (نسبة 16:9) — يمكن رفع أكبر مثل 1600×900 للجودة العالية.
          </div>
        </Field>

        {/* Upload video */}
        <Field label="فيديو (MP4/MOV/WebM — حتى 60 ميغا)" theme={theme}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v" ref={fileVid}
              onChange={() => upload("video", fileVid)} style={{ display: "none" }}
              data-testid="campaign-video-file" />
            <button onClick={() => fileVid.current?.click()} style={css.btnSecondary} disabled={uploadBusy}
              data-testid="campaign-video-upload">
              <Video size={14} strokeWidth={2.4} /> {uploadBusy ? "جارٍ الرفع…" : "رفع فيديو"}
            </button>
            {editing.video_path && (
              <button onClick={() => { u("video_path", null); u("video_url", null); }} style={css.btnGhostSmall}>
                إزالة
              </button>
            )}
            {editing.video_path && <span style={{ fontSize: 11, color: "#16a34a", fontWeight: 800 }}>✓ تم الرفع</span>}
          </div>
        </Field>

        <Field label="رابط فيديو خارجي (YouTube / MP4 مباشر) — اختياري" theme={theme}>
          <input value={editing.video_external_url} onChange={(e) => u("video_external_url", e.target.value)}
            style={{ ...css.input, direction: "ltr" }} placeholder="https://www.youtube.com/watch?v=..."
            data-testid="campaign-video-url" />
        </Field>

        <Field label="نص زر CTA (عربي) — اختياري" theme={theme}>
          <input value={editing.cta_label_ar} onChange={(e) => u("cta_label_ar", e.target.value)}
            style={css.input} placeholder="اضغط هنا" data-testid="campaign-cta-ar" />
        </Field>
        <Field label="نص زر CTA (English)" theme={theme}>
          <input value={editing.cta_label_en} onChange={(e) => u("cta_label_en", e.target.value)}
            style={{ ...css.input, direction: "ltr" }} placeholder="Learn more" data-testid="campaign-cta-en" />
        </Field>
        <Field label="رابط الزر" theme={theme}>
          <input value={editing.cta_url} onChange={(e) => u("cta_url", e.target.value)}
            style={{ ...css.input, direction: "ltr" }} placeholder="https://..." data-testid="campaign-cta-url" />
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="يبدأ في" theme={theme}>
            <input type="datetime-local" value={editing.start_at ? editing.start_at.slice(0, 16) : ""}
              onChange={(e) => u("start_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
              style={css.input} data-testid="campaign-start" />
          </Field>
          <Field label="ينتهي في" theme={theme}>
            <input type="datetime-local" value={editing.end_at ? editing.end_at.slice(0, 16) : ""}
              onChange={(e) => u("end_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
              style={css.input} data-testid="campaign-end" />
          </Field>
        </div>

        <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 6 }}>
          <button onClick={() => u("is_active", !editing.is_active)} style={css.iconBtn} data-testid="campaign-active-toggle">
            {editing.is_active ? <ToggleRight size={28} color="#16a34a" /> : <ToggleLeft size={28} color={theme.textMuted} />}
          </button>
          <span style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{editing.is_active ? "نشط" : "غير نشط"}</span>
          <div style={{ marginInlineStart: "auto" }}>
            <label style={{ fontSize: 11, color: theme.textMuted, marginInlineEnd: 6 }}>أولوية:</label>
            <input type="number" value={editing.weight} onChange={(e) => u("weight", e.target.value)}
              style={{ ...css.input, width: 90, textAlign: "center" }} data-testid="campaign-weight" />
          </div>
        </div>
      </div>

      {/* ───── Live Preview (Portal mockup) ───── */}
      <div style={css.previewBox}>
        <div style={css.previewHeader}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Eye size={14} strokeWidth={2.4} />
            <span>معاينة مطابقة لما يراه العميل</span>
          </div>
          <div style={{ display: "inline-flex", gap: 4, marginInlineStart: "auto" }}>
            {["ar", "en"].map((l) => (
              <button key={l} onClick={() => setPreviewLang(l)}
                data-testid={`campaign-preview-lang-${l}`}
                style={{
                  padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 800,
                  border: "none", cursor: "pointer",
                  background: previewLang === l ? theme.gold : "transparent",
                  color: previewLang === l ? "#0a0a0a" : theme.textMuted,
                }}>{l === "ar" ? "العربية" : "English"}</button>
            ))}
          </div>
        </div>
        <div style={{ background: theme.bg, borderRadius: 12, overflow: "hidden", padding: 0 }}>
          <PortalMockup
            campaign={previewCampaign}
            lang={previewLang} theme={theme} dir={previewLang === "ar" ? "rtl" : "ltr"}
            branding={branding} T={T}
          />
        </div>
        <p style={{ fontSize: 10, color: theme.textMuted, marginTop: 8, textAlign: "center" }}>
          العرض مصغّر للملاءمة — التصميم النهائي على البورتل بنفس النِسب ولون الخلفية.
        </p>
      </div>

      <div style={css.editorActions}>
        <button onClick={onCancel} style={css.btnGhost} data-testid="campaign-cancel">إلغاء</button>
        <button onClick={onSave} disabled={busy} style={css.btnGold} data-testid="campaign-save">
          <Save size={14} strokeWidth={2.6} /> {busy ? "جارٍ الحفظ…" : "حفظ"}
        </button>
      </div>
    </div>
  );
}

const Field = ({ label, theme, children }) => (
  <div>
    <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: theme.textMuted, marginBottom: 4 }}>{label}</label>
    {children}
  </div>
);

const makeStyles = (theme, dir) => ({
  page: { maxWidth: 1280, margin: "0 auto", padding: "24px 16px 60px" },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 12, marginBottom: 18, flexWrap: "wrap",
  },
  h1: { fontSize: 22, fontWeight: 900, color: theme.gold, margin: 0 },
  h2: { fontSize: 16, fontWeight: 900, color: theme.text, margin: 0 },
  sub: { fontSize: 12, color: theme.textMuted, marginTop: 4 },
  btnGold: {
    display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px",
    background: theme.goldGradient, color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none", borderRadius: 10, fontWeight: 900, fontSize: 13, cursor: "pointer",
  },
  btnGhost: {
    padding: "9px 16px", background: "transparent", color: theme.text,
    border: `1px solid ${theme.border}`, borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: "pointer",
  },
  btnGhostSmall: {
    padding: "5px 10px", background: "transparent", color: theme.errorText,
    border: `1px solid ${theme.errorBorder}`, borderRadius: 6, fontSize: 11, fontWeight: 800, cursor: "pointer",
  },
  btnSecondary: {
    display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
    background: theme.surfaceAlt, color: theme.text,
    border: `1px dashed ${theme.borderAccent}`, borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: "pointer",
  },
  err: {
    padding: "8px 14px", marginBottom: 12, borderRadius: 8,
    background: theme.errorBg, color: theme.errorText, fontSize: 13, fontWeight: 700,
  },
  empty: {
    padding: 28, textAlign: "center", color: theme.textMuted, fontSize: 13,
    border: `1px dashed ${theme.border}`, borderRadius: 12, background: theme.surface,
  },
  split: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.1fr)", gap: 16 },
  list: { display: "flex", flexDirection: "column", gap: 8 },
  row: {
    display: "flex", alignItems: "center", gap: 10, padding: 12,
    background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 12,
  },
  thumb: { width: 48, height: 48, borderRadius: 8, objectFit: "cover", flexShrink: 0 },
  iconBtn: {
    width: 32, height: 32, borderRadius: 8, display: "inline-flex", alignItems: "center", justifyContent: "center",
    background: "transparent", color: theme.text, border: `1px solid ${theme.border}`, cursor: "pointer",
  },
  iconBtnDanger: {
    width: 32, height: 32, borderRadius: 8, display: "inline-flex", alignItems: "center", justifyContent: "center",
    background: "transparent", color: theme.errorText, border: `1px solid ${theme.errorBorder}`, cursor: "pointer",
  },
  editor: {
    background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14,
    padding: 16, display: "flex", flexDirection: "column", gap: 14, position: "sticky", top: 80, alignSelf: "flex-start",
    maxHeight: "calc(100vh - 100px)", overflowY: "auto",
  },
  editorHeader: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  form: { display: "flex", flexDirection: "column", gap: 10 },
  input: {
    width: "100%", padding: "9px 12px", borderRadius: 8,
    background: theme.inputBg, color: theme.text,
    border: `1px solid ${theme.inputBorder}`, fontSize: 13, fontFamily: "inherit",
  },
  previewBox: {
    border: `1px dashed ${theme.borderAccent}`, borderRadius: 14, padding: 12, background: theme.surfaceAlt,
  },
  previewHeader: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 900,
    color: theme.gold, marginBottom: 8, letterSpacing: 0.3, textTransform: "uppercase",
  },
  editorActions: { display: "flex", gap: 8, justifyContent: dir === "rtl" ? "flex-start" : "flex-end" },
});
